import {
  BooleanInput,
  DateTimeInput,
  required,
  SelectInput,
  TextInput,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { Box, Stack, Typography, Button as MUIButton, Tooltip, IconButton } from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { chains, types } from 'src/assets/data';
import useTokenXstocks from './useTokenXstocks';

interface FormProps {
  isCreate: boolean;
}
const TokenStockForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const metadata = watch('metadata') || record?.metadata || {};
  const { findToken } = useTokenXstocks();
  const [addressMessage, setAddressMessage] = useState('');
  const [chainMessage, setChainMessage] = useState('');
  const chainId = useWatch({ name: 'chainId' });
  const address = useWatch({ name: 'address' });
  const [noCreate, setNoCreate] = useState<boolean>(true);

  useEffect(() => {
    isCreate && handleOnBlurAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const handleOnBlurAddress = async () => {
    setAddressMessage('');
    setChainMessage('');

    if (!address || chainId === '' || chainId === undefined) {
      return;
    }

    try {
      const token = await findToken(address, chainId);
      if (!token) {
        return;
      }
      if (token.validate) {
        setAddressMessage(t('module.xStocks.validate.conflict'));
        return;
      }
      if (token.code && token.code === 'CHAIN_NOT_FOUND') {
        setChainMessage(token.message);
        return;
      }
      setNoCreate(true);
    } catch (error: any) {
      setAddressMessage(t('module.tokenMetadata.validate.addressNotExists'));
    }
  };

  const hasTypedRef = useRef(false);

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenMetadata.address')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              source="address"
              label=""
              placeholder={t('module.tokenMetadata.address')}
              parse={(value) => {
                if (!value) return undefined;
                const items = value.split(',').map((v: string) => v.trim());
                return items.length === 1 ? items[0] : items;
              }}
              validate={[
                required(),
                () => {
                  if (addressMessage !== '') {
                    return addressMessage;
                  }
                  return undefined;
                },
              ]}
              disabled={!isCreate}
              fullWidth
              helperText={<span style={{ color: 'red' }}>{addressMessage}</span>}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onChange={() => {
                hasTypedRef.current = true;
              }}
              onBlur={(e) => {
                if (isCreate && hasTypedRef.current) {
                  handleOnBlurAddress();
                  hasTypedRef.current = false;
                }
              }}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenMetadata.chain')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              label={false}
              disabled={!isCreate}
              source="chainId"
              defaultValue={501424}
              validate={[
                required(),
                () => {
                  if (chainMessage !== '') {
                    return chainMessage;
                  }
                  return undefined;
                },
              ]}
              fullWidth
              helperText={<span style={{ color: 'red' }}>{chainMessage}</span>}
              choices={chains}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenOnchainInfo.type')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              label={false}
              disabled={!isCreate}
              source="type"
              defaultValue={'not_found'}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              choices={types}
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default TokenStockForm;
